import { ChevronDown, Plus } from "@tamagui/lucide-icons";
import React, { useEffect, useId, useState } from "react";
import { LayoutChangeEvent } from "react-native";
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { Card, CardProps, View, YStack } from "tamagui";

import { Button } from "@/components/Button";

type ToggleIconType = "plus" | "chevron";
interface ExpandableCardProps extends Omit<CardProps, "content"> {
  title: React.ReactNode;
  content: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  toggleIconType?: ToggleIconType;
}

export default function ExpandableCard({
  title,
  content,
  isExpanded,
  onToggle,
  toggleIconType,
  ...props
}: ExpandableCardProps) {
  const contentId = useId();
  const height = useSharedValue(0);
  const opacity = useSharedValue(0);
  const [contentHeight, setContentHeight] = useState(0);

  const onContentLayout = (event: LayoutChangeEvent) => {
    const measuredHeight = event.nativeEvent.layout.height;
    if (measuredHeight === 0) return;
    setContentHeight(measuredHeight);
  };

  useEffect(() => {
    height.value = withTiming(isExpanded ? contentHeight : 0, {
      duration: 300,
    });
    opacity.value = withTiming(isExpanded ? 1 : 0, { duration: 300 });
  }, [isExpanded, contentHeight, height, opacity]);

  const animatedStyle = useAnimatedStyle(
    () => ({
      height: height.value,
      opacity: opacity.value,
      overflow: "hidden",
    }),
    [height, opacity],
  );

  let iconRotation = 0;
  if (toggleIconType === "chevron" && isExpanded) iconRotation = 180;
  else if (toggleIconType === "plus" && isExpanded) iconRotation = 45;

  return (
    <Card backgroundColor="transparent" {...props}>
      <Button.Wrapper
        onPress={onToggle}
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap="$300"
        aria-expanded={isExpanded}
        aria-controls={contentId}
        dataTrackingId={`expandable-card-toggle-${title}`}
      >
        {title}
        <View transition="standard" rotate={`${iconRotation}deg`}>
          {toggleIconType === "plus" ? <Plus /> : <ChevronDown />}
        </View>
      </Button.Wrapper>
      <Animated.View
        id={contentId}
        style={animatedStyle}
        aria-hidden={!isExpanded}
      >
        <YStack
          justifyContent="flex-start"
          onLayout={onContentLayout}
          position="absolute"
          opacity={0}
          pointerEvents="none"
          aria-hidden
          paddingRight="$400"
          paddingTop="$100"
        >
          {content}
        </YStack>
        <YStack
          justifyContent="flex-start"
          paddingRight="$400"
          paddingTop="$100"
        >
          {content}
        </YStack>
      </Animated.View>
    </Card>
  );
}
